import {
    Button,
    DialogTrigger,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerHeaderTitle,
    makeStyles,
    mergeClasses,
    MessageBar,
    MessageBarActions,
    MessageBarBody,
    OverlayDrawer,
    Spinner,
    tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular, DismissRegular } from '@fluentui/react-icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFeatureEnabled } from '../../../api/features/features';
import { useSaveCalculationOptions } from '../../../api/report/report';
import { usePreventNavigation } from '../../../hooks/usePreventNavigation';
import { useTaxReturnCalculationState } from '../../../hooks/useTaxReturnCalculationState';
import { CalculationOptionRequest, ICalculationOptions, IWhitePaperColumn } from '../../../model';
import { EditableOptionsPanelEnabled } from '../../../utils/FeatureFlags.constants';
import { HEADER_SIZE } from '../../../utils/Style.constants';
import ConfirmationDialogV9 from '../../dialog/ConfirmationDialogV9';
import CalculationOptionsTable from './CalculationOptionsTable';
import CalculationOptionsTabList from './CalculationOptionsTabList';

export interface ICalculationOptionsPanelProps {
    calculationOptions: ICalculationOptions[];
    entityCode: string;
    isOpen: boolean;
    columns?: IWhitePaperColumn[] | null;
    onClose?: () => void;
    jurisdiction?: string;
    period?: string;
    case?: string;
    taxReturnKey?: number;
    year?: number | null;
    reportName?: string | null;
    taxReturnItemId?: string;
}

interface ICalculationOptionChange {
    rowNumber: number;
    columnId: number;
    value: string;
}

const useStyles = makeStyles({
    drawer: {
        top: HEADER_SIZE,
        height: `calc(100% - ${HEADER_SIZE})`,
        transitionProperty: 'width',
        transitionDuration: '500ms',
    },
    drawerEditable: {
        width: '53rem',
        maxWidth: '100vw',
    },
    drawerReadonly: {
        minWidth: '39rem',
        maxWidth: '50rem',
        width: 'auto',
    },
    drawerHeader: {
        paddingTop: tokens.spacingVerticalL,
        paddingBottom: 0,
        paddingLeft: tokens.spacingHorizontalXXL,
        paddingRight: tokens.spacingHorizontalXL,
    },
    headerTitle: {
        fontSize: '18px',
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: '28px',
        letterSpacing: '-0.256px',
    },
    drawerBody: {
        paddingTop: tokens.spacingVerticalS,
        paddingRight: tokens.spacingHorizontalXL,
        paddingBottom: tokens.spacingVerticalXL,
        paddingLeft: tokens.spacingHorizontalXXL,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalSNudge,
        overflowY: 'auto',
    },
    drawerBodyWithSuccess: {
        paddingTop: tokens.spacingVerticalSNudge,
    },
    contentContainer: {
        position: 'relative',
        minHeight: '75vh',
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: tokens.colorNeutralBackground1,
        zIndex: 10,
    },
    footer: {
        justifyContent: 'flex-end',
        gap: tokens.spacingHorizontalS,
    },
    processingSpinner: {
        paddingLeft: tokens.spacingHorizontalXXS,
        opacity: 0.5,
    },
});

export const CalculationOptionsPanel = ({
    calculationOptions,
    isOpen,
    columns,
    onClose,
    jurisdiction,
    period,
    case: caseValue,
    taxReturnKey,
    entityCode,
    year,
    reportName,
    taxReturnItemId,
}: ICalculationOptionsPanelProps) => {
    const { t } = useTranslation();
    const styles = useStyles();
    const [changes, setChanges] = useState<Map<string, ICalculationOptionChange>>(new Map());
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isGridReady, setIsGridReady] = useState(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [savedChanges, setSavedChanges] = useState<Map<string, ICalculationOptionChange>>(new Map());
    const changesRef = useRef(changes);
    // Todo: we should simplify this component's change tracking logic, it makes it difficult to tap into the current state
    // hasUnsavedChanges should be const and we might not need changesRef as an additional variable
    let hasUnsavedChanges = false;
    for (const [key, change] of changes.entries()) {
        const savedChange = savedChanges.get(key);
        if (!savedChange || savedChange.value !== change.value) {
            hasUnsavedChanges = true;
            break;
        }
    }
    const { blocker, setShouldBlockNavigation } = usePreventNavigation();

    useEffect(() => {
        changesRef.current = changes;
    }, [changes]);

    const saveCalculationOptionsMutation = useSaveCalculationOptions();
    const { rerunCalculationReport } = useTaxReturnCalculationState();
    const { data: isEditableOptionsPanelEnabled, isLoading: isLoadingFeatureFlag } = useIsFeatureEnabled({
        featureFlagName: EditableOptionsPanelEnabled,
    });

    useEffect(() => {
        if (!isOpen) {
            setChanges(new Map());
            setSavedChanges(new Map());
            setShowSuccessMessage(false);
            setIsSaving(false);
            setIsGridReady(false);
            setShowConfirmationDialog(false);
            setShouldBlockNavigation(false);
        } else {
            setShouldBlockNavigation(hasUnsavedChanges);
        }
    }, [isOpen, hasUnsavedChanges, setShouldBlockNavigation]);

    useEffect(() => {
        const currentChanges = changesRef.current;
        if (currentChanges.size > 0 && calculationOptions && columns) {
            let allChangesApplied = true;
            for (const change of currentChanges.values()) {
                const option = calculationOptions.find((opt) => opt.rowNumber === change.rowNumber.toString());
                if (!option) {
                    allChangesApplied = false;
                    break;
                }
                const column = columns.find((col) => col.originalColumnId === change.columnId);
                if (!column?.fieldName) {
                    allChangesApplied = false;
                    break;
                }
                const currentValue = option.value?.[column.fieldName]?.current?.toString() || '';
                if (currentValue !== change.value) {
                    allChangesApplied = false;
                    break;
                }
            }
            if (allChangesApplied) {
                setChanges(new Map());
                setSavedChanges(new Map());
            }
        }
    }, [calculationOptions, columns]);

    useEffect(() => {
        if (blocker && blocker.state === 'blocked' && !showConfirmationDialog && !isSaving) {
            setShowConfirmationDialog(true);
        }
    }, [blocker, showConfirmationDialog, isSaving]);

    const handleGridReady = useCallback(() => {
        setIsGridReady(true);
    }, []);

    const handleCellEdit = useCallback(
        (rowNumber: string | null, columnId: number, newValue: string) => {
            if (!rowNumber) {
                return;
            }

            const rowNum = parseInt(rowNumber, 10);
            if (isNaN(rowNum)) {
                return;
            }

            const changeKey = `${rowNum}-${columnId}`;

            setChanges((prevChanges) => {
                const newChanges = new Map(prevChanges);
                newChanges.set(changeKey, {
                    rowNumber: rowNum,
                    columnId,
                    value: newValue,
                });
                return newChanges;
            });

            if (showSuccessMessage) {
                setShowSuccessMessage(false);
            }
        },
        [showSuccessMessage]
    );

    const handleSave = async () => {
        if (changes.size === 0 || !taxReturnKey) {
            return;
        }

        const unsavedChanges: [string, ICalculationOptionChange][] = [];
        for (const [key, change] of changes.entries()) {
            const savedChange = savedChanges.get(key);
            if (!savedChange || savedChange.value !== change.value) {
                unsavedChanges.push([key, change]);
            }
        }

        if (unsavedChanges.length === 0) {
            return;
        }

        setIsSaving(true);

        const periodValue = year?.toString() ?? period;

        const calculationOptionRequests: CalculationOptionRequest[] = unsavedChanges.map(([_, change]) => ({
            rowNumber: change.rowNumber,
            columnId: change.columnId,
            value: change.value,
            entityCode: entityCode,
            jurisdictionCode: jurisdiction,
            period: periodValue,
            case: caseValue,
        }));

        try {
            const response = await saveCalculationOptionsMutation.mutateAsync({
                data: {
                    calculationOptionRequests,
                    taxReturnKey,
                },
            });

            if (response && !response.errors?.length) {
                setShowSuccessMessage(true);
                const newSavedChanges = new Map(savedChanges);
                changes.forEach((change, key) => {
                    newSavedChanges.set(key, change);
                });
                setSavedChanges(newSavedChanges);

                if (taxReturnItemId) {
                    rerunCalculationReport(reportName, taxReturnKey, taxReturnItemId);
                }
            }
        } catch (error) {
            console.error('Error saving calculation options:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        if (hasUnsavedChanges) {
            setShowConfirmationDialog(true);
        } else {
            setChanges(new Map());
            setShowSuccessMessage(false);
            onClose?.();
        }
    };

    const handleDrawerOpenChange = (_ev: unknown, data: { open: boolean }) => {
        if (!data.open) {
            if (hasUnsavedChanges) {
                setShowConfirmationDialog(true);
            } else {
                onClose?.();
            }
        }
    };

    const onConfirmationSave = async (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        setShowConfirmationDialog(false);
        await handleSave();
        onClose?.();
        setShouldBlockNavigation(false);
        if (blocker.state === 'blocked') {
            blocker.proceed();
        }
    };

    const onConfirmationDiscard = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        onClose?.();
        setShouldBlockNavigation(false);
        if (blocker.state === 'blocked') {
            blocker.proceed();
        }
    };

    const onConfirmationCancel = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        setShowConfirmationDialog(false);
        if (blocker.state === 'blocked') {
            blocker.reset();
        }
    };

    const isSaveDisabled = !hasUnsavedChanges || isSaving;
    const isLoading = isLoadingFeatureFlag || isEditableOptionsPanelEnabled === undefined || !isGridReady;

    return (
        <>
            <OverlayDrawer
                open={isOpen}
                onOpenChange={handleDrawerOpenChange}
                position='end'
                modalType='modal'
                data-testid='calculationOptionsPanelTestId'
                className={mergeClasses(styles.drawer, isEditableOptionsPanelEnabled ? styles.drawerEditable : styles.drawerReadonly)}
            >
                <DrawerHeader className={styles.drawerHeader}>
                    <DrawerHeaderTitle
                        action={
                            <Button
                                appearance='subtle'
                                icon={<Dismiss24Regular />}
                                aria-label={t('close').toString()}
                                onClick={handleCancel}
                            />
                        }
                    >
                        <span className={styles.headerTitle}>{t('calculationOptions').toString()}</span>
                    </DrawerHeaderTitle>
                </DrawerHeader>

                <DrawerBody className={mergeClasses(styles.drawerBody, showSuccessMessage && styles.drawerBodyWithSuccess)}>
                    {showSuccessMessage && (
                        <MessageBar intent='success'>
                            <MessageBarBody>{t('changesSavedSuccessfully')}</MessageBarBody>
                            <MessageBarActions
                                containerAction={
                                    <Button
                                        appearance='transparent'
                                        icon={<DismissRegular />}
                                        onClick={() => setShowSuccessMessage(false)}
                                        aria-label={t('close').toString()}
                                    />
                                }
                            />
                        </MessageBar>
                    )}
                    <div className={styles.contentContainer}>
                        {isLoading && (
                            <div className={styles.loadingOverlay}>
                                <Spinner size='extra-large' label={t('loading').toString()} />
                            </div>
                        )}
                        <div style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
                            {!isLoadingFeatureFlag &&
                                isEditableOptionsPanelEnabled !== undefined &&
                                (isEditableOptionsPanelEnabled ? (
                                    <CalculationOptionsTabList
                                        calculationOptions={calculationOptions}
                                        columns={columns}
                                        onCellEdit={handleCellEdit}
                                        isEditingEnabled={isEditableOptionsPanelEnabled}
                                        onGridReady={handleGridReady}
                                        pendingChanges={changes}
                                    />
                                ) : (
                                    <CalculationOptionsTable
                                        calculationOptions={calculationOptions}
                                        columns={columns}
                                        onCellEdit={handleCellEdit}
                                        isEditingEnabled={isEditableOptionsPanelEnabled}
                                        onGridReady={handleGridReady}
                                    />
                                ))}
                        </div>
                    </div>
                </DrawerBody>

                {isEditableOptionsPanelEnabled ? (
                    <DrawerFooter className={styles.footer}>
                        <Button
                            appearance='primary'
                            aria-label={t('saveAndCalculate').toString()}
                            onClick={handleSave}
                            disabled={isSaveDisabled}
                        >
                            {isSaving ? t('processing').toString() : t('saveAndCalculate').toString()}
                            {isSaving && <Spinner size='extra-tiny' className={styles.processingSpinner} />}
                        </Button>
                        <Button appearance='secondary' aria-label={t('cancel').toString()} onClick={handleCancel}>
                            {t('cancel').toString()}
                        </Button>
                    </DrawerFooter>
                ) : null}
            </OverlayDrawer>

            {/* Keep outside OverlayDrawer — nested Dialogs are aria-hidden by Tabster's modalizer. */}
            <ConfirmationDialogV9
                open={showConfirmationDialog}
                title={t('confirm')}
                content={t('unsavedCalculationOptionsConfirmationMessage')}
                buttons={
                    <>
                        <DialogTrigger>
                            <Button
                                onClick={onConfirmationCancel}
                                data-analytics-button-name='confirmation_dialog_cancel'
                                aria-label={t('cancel').toString()}
                            >
                                {t('cancel')}
                            </Button>
                        </DialogTrigger>
                        <Button
                            appearance='primary'
                            onClick={onConfirmationSave}
                            data-analytics-button-name='confirmation_dialog_save'
                            aria-label={t('save').toString()}
                        >
                            {t('save')}
                        </Button>
                        <Button
                            onClick={onConfirmationDiscard}
                            data-analytics-button-name='confirmation_dialog_discard'
                            aria-label={t('leave').toString()}
                        >
                            {t('leave')}
                        </Button>
                    </>
                }
            />
        </>
    );
};
